import { useContext } from "react";
import { Field, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { useIntl } from "react-intl";
import Section from "../../../common/section";
import * as Validations from "../../../common/form/validator";
import { TextInput, SwitchInput, DropdownInput } from "../../../common/fields";
import { Button } from "primereact/button";
import { FormContext } from "../../../common/context";
import { withSelected } from "../../../common/hooks/useServerSearch";
import {
  GlobalConstants,
  LearningProgramConstants,
} from "../../../../constants";

const LearningPathwayDetailsForm = ({
  disabled = false,
  learningMilestones = [],
  recordId,
  certificates,
  rewards = [],
  // Search the server as the admin types in the milestone and reward pickers
  onMilestoneSearch,
  onRewardSearch,
  // The program's current reward ({ id, name }), shown even when a search left it out
  selectedReward,
}) => {
  const intl = useIntl();
  const formState = useFormState();
  const formProvider = useContext(FormContext);
  return (
    <div className="flex flex-column gap-4">
      <Section title="identity">
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="name"
              component={TextInput}
              type="text"
              label={intl.formatMessage({
                id: "name",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "name",
              })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="isLinear"
              component={SwitchInput}
              type="checkbox"
              label={intl.formatMessage({
                id: "is_linear",
              })}
              readOnly={disabled}
              placeholder={intl.formatMessage({
                id: "is_linear",
              })}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="certificateId"
              component={DropdownInput}
              label={intl.formatMessage({
                id: "certficate",
              })}
              placeholder={intl.formatMessage({
                id: "certficate",
              })}
              disabled={disabled}
              filter={true}
              options={certificates.map((item) => ({
                name: item.name,
                id: item.id,
                value: item.id,
                i18n: false,
              }))}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="type"
              component={DropdownInput}
              label={intl.formatMessage({
                id: "type",
              })}
              options={LearningProgramConstants.TYPES}
              disabled={disabled}
              // Static constants list; disable filter
              filter={false}
              placeholder={intl.formatMessage({
                id: "type",
              })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
          {/* Reward Assignment - Simple fields without triggers */}
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="rewardId"
              component={DropdownInput}
              label={intl.formatMessage({
                id: "reward",
              })}
              placeholder={intl.formatMessage({
                id: "reward",
              })}
              disabled={disabled}
              filter={true}
              options={withSelected(
                (rewards || []).map((item) => ({
                  name: item.name,
                  id: item.id,
                  value: item.id,
                  i18n: false,
                })),
                selectedReward && {
                  ...selectedReward,
                  value: selectedReward.id,
                  i18n: false,
                }
              )}
              onFilter={onRewardSearch}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="pointsOnCompletion"
              component={TextInput}
              type="number"
              label={intl.formatMessage({
                id: "point_on_completion",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "point_on_completion",
              })}
              validate={Validations.numberPositive}
            />
          </div>
          {(formState.values?.type === 1 && (
            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="isRepetable"
                disabled={recordId ? true : false}
                component={SwitchInput}
                type="checkbox"
                label={intl.formatMessage({
                  id: "re_compliance",
                })}
                placeholder={intl.formatMessage({
                  id: "re_compliance",
                })}
              />
            </div>
          )) ||
            null}
          {(formState.values?.type === 1 && formState?.values?.isRepetable && (
            <div className="col-12 md:col-6 lg:col-4">
              <Field
                disabled={recordId ? true : false}
                name="repetableInMonths"
                component={TextInput}
                type="number"
                label={intl.formatMessage({
                  id: "repeat_every_months",
                })}
                placeholder={intl.formatMessage({
                  id: "repeat_every_months",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
          )) ||
            null}
          {/* <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="tags"
              component={Tags}
              type="text"
              label={intl.formatMessage({
                id: "tags",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "tags",
              })}
            />
          </div> */}
        </div>
      </Section>
      {(!recordId && (
        <Section title="learning_milestones">
          <FieldArray name="milestones">
            {({ fields }) =>
              fields.map((name, index) => {
                const filterFields = fields?.value?.filter(
                  (_, i_index) => i_index !== index
                );
                return (
                  <div className="grid" key={index}>
                    {(index > 0 && (
                      <div className="col-12 md:col-2 lg:col-2">
                        <Field
                          name={`${name}.operator`}
                          component={DropdownInput}
                          options={GlobalConstants.LEARNING_OPERATORS.map(
                            (item) => ({
                              name: intl.formatMessage({ id: item.name }),
                              id: item.id,
                            })
                          )}
                          readOnly={index === 0}
                          // Constant operators list; disable filter
                          filter={false}
                          label={intl.formatMessage({
                            id: "must_have",
                          })}
                          placeholder={intl.formatMessage({
                            id: "must_have",
                          })}
                          required
                          validate={Validations.composeValidators(
                            Validations.required
                          )}
                        />
                      </div>
                    )) || (
                      <div className="col-12 md:col-2 lg:col-2 flex align-items-center">
                        {intl.formatMessage({ id: "specify_rules" })}
                      </div>
                    )}
                    <div className="col-12 md:col-6 lg:col-6">
                      <div className="flex gap-2">
                        <div className="flex-grow-1">
                          <Field
                            name={`${name}.learningMilestone`}
                            component={DropdownInput}
                            options={withSelected(
                              learningMilestones
                                .filter(
                                  (item) =>
                                    !filterFields
                                      ?.map((item) => item.learningMilestone?.id)
                                      ?.includes(item.id)
                                )
                                .map((item) => ({
                                  name: `${item.name}`,
                                  id: item.id,
                                })),
                              // The row's own choice, which a search may have left out
                              fields.value?.[index]?.learningMilestone
                            )}
                            onFilter={onMilestoneSearch}
                            label={intl.formatMessage({
                              id: "learning_milestone",
                            })}
                            placeholder={intl.formatMessage({
                              id: "learning_milestone",
                            })}
                            filter={true}
                            showClear
                            required
                            validate={Validations.composeValidators(
                              Validations.required
                            )}
                          />
                        </div>
                        {(index && (
                          <Button
                            type="button"
                            icon="pi pi-arrow-up"
                            style={{ marginTop: "21px" }}
                            onClick={() => {
                              const current = fields.value[index];
                              const replace = fields.value[index - 1];
                              formProvider.update("milestones", index, {
                                ...replace,
                                operator: current.operator,
                              });
                              formProvider.update("milestones", index - 1, {
                                ...current,
                                operator: replace.operator,
                              });
                            }}
                          />
                        )) ||
                          null}
                        {(index && index !== fields.length - 1 && (
                          <Button
                            type="button"
                            icon="pi pi-arrow-down"
                            style={{ marginTop: "21px" }}
                            onClick={() => {
                              const current = fields.value[index];
                              const replace = fields.value[index + 1];
                              formProvider.update("milestones", index, {
                                ...replace,
                                operator: current.operator,
                              });
                              formProvider.update("milestones", index + 1, {
                                ...current,
                                operator: replace.operator,
                              });
                            }}
                          />
                        )) ||
                          null}
                      </div>
                    </div>
                    <div className="col-12 md:col-12 lg:col-2">
                      {(index === 0 && (
                        <>
                          <Button
                            type="button"
                            icon="pi pi-plus"
                            style={{ marginTop: "21px" }}
                            onClick={() =>
                              formProvider.push("milestones", {
                                operator:
                                  GlobalConstants.LEARNING_OPERATORS.filter(
                                    (op) => op.default
                                  ).map((item) => ({
                                    name: intl.formatMessage({ id: item.name }),
                                    id: item.id,
                                  }))[0],
                              })
                            }
                          />
                        </>
                      )) || (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            icon="pi pi-trash"
                            style={{ marginTop: "21px" }}
                            onClick={() => fields.remove(index)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            }
          </FieldArray>
        </Section>
      )) ||
        null}
    </div>
  );
};

export default LearningPathwayDetailsForm;
